import React, { useCallback, useId, useLayoutEffect, useRef } from "react";
import { Send, Check, X, Loader2 } from "lucide-react";
import { Tooltip } from "../../atoms/Tooltip";
import { VisuallyHidden } from "../../atoms/VisuallyHidden";
import {
  VOICE_CANCEL_LABEL,
  VOICE_CONFIRM_LABEL,
  VOICE_HINT,
  VOICE_START_LABEL,
} from "../../../services/speech/speech.copy";
import { resizeTextareaToContent } from "../../../utils/autoGrowTextarea";
import { generateWaveformBars } from "../../../utils/generateWaveformBars";
import {
  Container,
  ActionsRow,
  InputColumn,
  InlineHint,
  WidgetContextPill,
  WidgetContextClear,
  WidgetContextLabel,
  WidgetContextName,
  PromptInput,
  GhostIconButton,
  SendIconButton,
  SpinnerIcon,
  FileChipsRow,
  FileChip,
  FileTypeBadge,
  FileChipName,
  RemoveFileButton,
  WaveformRow,
  WaveformBarsContainer,
  WaveBar,
  RecordActionBtn,
  RecordingInputWrapper,
  RecordingLayout,
} from "./PromptInputBar.styles";
import type { PromptInputBarProps } from "./PromptInputBar.types";

// Fixed 3px bar width (see WaveBar); pattern (count/heights/dim ratio) is configured in
// generateWaveformBars.ts — edit DEFAULT_WAVEFORM_PATTERN there to reshape the waveform.
const WAVEFORM_BARS = generateWaveformBars();

const AttachmentIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M14.2929 7.36645L8.16621 13.4931C7.41565 14.2437 6.39767 14.6653 5.33621 14.6653C4.27475 14.6653 3.25677 14.2437 2.50621 13.4931C1.75565 12.7425 1.33398 11.7246 1.33398 10.6631C1.33398 9.60166 1.75565 8.58367 2.50621 7.83311L8.21954 2.11978C8.71992 1.61852 9.39892 1.33656 10.1072 1.33594C10.8154 1.33531 11.495 1.61607 11.9962 2.11645C12.4975 2.61682 12.7794 3.29583 12.78 4.00409C12.7807 4.71235 12.4999 5.39185 11.9995 5.89311L6.27288 11.6064C6.02269 11.8566 5.68336 11.9972 5.32954 11.9972C4.97572 11.9972 4.6364 11.8566 4.38621 11.6064C4.13602 11.3563 3.99547 11.0169 3.99547 10.6631C3.99547 10.3093 4.13602 9.96997 4.38621 9.71978L10.0462 4.06645"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AudioWaveIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M11 11.3346V4.66797"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5 11.3346V4.66797"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 13.3346V2.66797"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2 9.33464V6.66797"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14 9.33464V6.66797"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const WaveformSection = ({
  onRecordConfirm,
  onRecordCancel,
}: {
  onRecordConfirm?: () => void;
  onRecordCancel?: () => void;
}) => (
  <WaveformRow>
    <WaveformBarsContainer aria-label="Recording in progress" role="img">
      {WAVEFORM_BARS.map((bar, i) => (
        <WaveBar key={i} $h={bar.h} $dim={bar.dim} $delay={i * 0.055} />
      ))}
    </WaveformBarsContainer>
    <RecordActionBtn onClick={onRecordConfirm} aria-label={VOICE_CONFIRM_LABEL}>
      <Check size={16} aria-hidden />
    </RecordActionBtn>
    <RecordActionBtn onClick={onRecordCancel} aria-label={VOICE_CANCEL_LABEL}>
      <X size={16} aria-hidden />
    </RecordActionBtn>
  </WaveformRow>
);

export const PromptInputBar: React.FC<PromptInputBarProps> = ({
  variant = "default",
  placeholder = "What data story would you like to create?",
  value = "",
  onChange,
  isRecording = false,
  transcribedText = "",
  onRecordConfirm,
  onRecordCancel,
  uploadedFiles = [],
  onRemoveFile,
  onUpload,
  hideUpload = false,
  hint,
  onAudioClick,
  audioDisabledReason = null,
  onSend,
  disabled = false,
  isLoading = false,
  selectedWidgetName,
  onClearContext,
  clearContextLabel,
  storyTitle,
  inputRef,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const micReasonId = useId();
  // The bar auto-sizes through its own ref; the parent's `inputRef` gets the same node.
  const setTextareaRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      textareaRef.current = node;
      if (typeof inputRef === "function") inputRef(node);
      else if (inputRef) (inputRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
    },
    [inputRef],
  );
  const assistant = variant === "assistant";
  const compact = variant === "compact" || assistant;
  const hasFiles = uploadedFiles.length > 0;
  const displayValue = isRecording ? transcribedText || value : value;
  const contextLabel = selectedWidgetName ? "Chart:" : "Story:";
  const contextName = selectedWidgetName || storyTitle;
  // Nothing stacked above the textarea (no scope pill, no attachments) → keep
  // the bar on one short row instead of reserving a second row for the actions.
  const isInline = !contextName && !hasFiles && !assistant;

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (!isRecording) {
        resizeTextareaToContent(e.target);
      }
      onChange?.(e.target.value);
    },
    [onChange, isRecording],
  );

  useLayoutEffect(() => {
    if (isRecording || !textareaRef.current) return;
    resizeTextareaToContent(textareaRef.current);
  }, [value, isRecording, hasFiles]);

  const fileChips = hasFiles ? (
    <FileChipsRow>
      {uploadedFiles.map((f) => (
        <FileChip key={f.id}>
          <FileTypeBadge $fileType={f.fileType} aria-hidden="true">
            {f.fileType === "other" ? "FILE" : f.fileType.toUpperCase()}
          </FileTypeBadge>
          <FileChipName title={f.name}>{f.name}</FileChipName>
          <RemoveFileButton
            onClick={() => onRemoveFile?.(f.id)}
            aria-label={`Remove ${f.name}`}
          >
            <X size={12} aria-hidden />
          </RemoveFileButton>
        </FileChip>
      ))}
    </FileChipsRow>
  ) : null;

  if (isRecording) {
    return (
      <RecordingLayout>
        {fileChips}
        <RecordingInputWrapper>
          <PromptInput
            ref={setTextareaRef}
            rows={1}
            value={displayValue}
            onChange={handleInputChange}
            placeholder={placeholder}
            aria-label={placeholder}
          />
        </RecordingInputWrapper>
        <WaveformSection
          onRecordConfirm={onRecordConfirm}
          onRecordCancel={onRecordCancel}
        />
      </RecordingLayout>
    );
  }

  return (
    <Container $inline={isInline} $compact={compact} $assistant={assistant} aria-disabled={disabled || undefined}>
      {fileChips}
      <InputColumn $inline={isInline}>
        {contextName && (
          <WidgetContextPill>
            <WidgetContextLabel>{contextLabel}</WidgetContextLabel>
            <WidgetContextName title={contextName}>
              {contextName}
            </WidgetContextName>
            {selectedWidgetName && onClearContext && (
              <WidgetContextClear type="button" onClick={onClearContext} aria-label={clearContextLabel} disabled={disabled}>
                <X size={12} aria-hidden />
              </WidgetContextClear>
            )}
          </WidgetContextPill>
        )}
        <PromptInput
          ref={setTextareaRef}
          rows={1}
          value={value}
          onChange={handleInputChange}
          placeholder={placeholder}
          aria-label={placeholder}
          disabled={disabled}
        />
      </InputColumn>
      <ActionsRow $inline={isInline}>
        {assistant && hint ? <InlineHint>{hint}</InlineHint> : null}
        {hideUpload ? null : (
          <GhostIconButton
            $compact={compact}
            onClick={onUpload}
            aria-label="Upload file"
            disabled={disabled}
          >
            <AttachmentIcon />
          </GhostIconButton>
        )}
        {audioDisabledReason ? (
          <Tooltip label={audioDisabledReason} position="top" multiline>
            <GhostIconButton
              $compact={compact}
              aria-label={VOICE_START_LABEL}
              aria-disabled="true"
              aria-describedby={micReasonId}
            >
              <AudioWaveIcon />
            </GhostIconButton>
            <VisuallyHidden id={micReasonId}>{audioDisabledReason}</VisuallyHidden>
          </Tooltip>
        ) : (
          <Tooltip label={VOICE_HINT} position="top" multiline>
            <GhostIconButton
          $compact={compact}
              onClick={onAudioClick}
              aria-label={VOICE_START_LABEL}
              disabled={disabled}
            >
              <AudioWaveIcon />
            </GhostIconButton>
          </Tooltip>
        )}
        <SendIconButton
          $compact={compact}
          $assistant={assistant}
          onClick={onSend}
          aria-label={isLoading ? "Sending…" : "Send prompt"}
          disabled={disabled || isLoading}
        >
          {isLoading ? (
            <SpinnerIcon>
              <Loader2 size={16} aria-hidden />
            </SpinnerIcon>
          ) : (
            <Send size={16} aria-hidden />
          )}
        </SendIconButton>
      </ActionsRow>
    </Container>
  );
};

export default PromptInputBar;
